import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { PendingDose } from '@rondela/shared'
import type { TimelineBatch as TimelineBatchType, TimelineItem } from '../lib/timeline'
import { formatBatchTimeRange, getTimeOfDayIcon } from '../lib/timeline'
import { TimelineItemRow, TimelineItemDetail } from './TimelineItemRow'

interface TimelineBatchProps {
  batch: TimelineBatchType
  onDoseTaken: (dose: PendingDose) => void
  onDoseMissed: (dose: PendingDose) => void
  isLoading?: boolean
}

/**
 * Renders a batch of timeline items (within 2-hour window)
 * with expandable details section
 */
export function TimelineBatch({
  batch,
  onDoseTaken,
  onDoseMissed,
  isLoading = false,
}: TimelineBatchProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const timeRange = formatBatchTimeRange(batch.startTime, batch.endTime)
  const timeOfDayIcon = getTimeOfDayIcon(batch.startTime.getHours())

  // Check if any item has details to show
  const hasDetails = batch.items.some((item) => {
    if (item.type === 'dose') return !!item.data.instrucciones
    if (item.type === 'appointment') return !!item.data.veterinario || !!item.data.clinica
    return false
  })

  const handleDoseAction = (item: TimelineItem, action: 'taken' | 'missed') => {
    if (item.type === 'dose') {
      if (action === 'taken') {
        onDoseTaken(item.data)
      } else {
        onDoseMissed(item.data)
      }
    }
  }

  return (
    <div className={`timeline-batch ${isExpanded ? 'timeline-batch--expanded' : ''}`}>
      {/* Batch Header */}
      <button
        type="button"
        className="timeline-batch__header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        disabled={!hasDetails}
      >
        <span className="timeline-batch__time-icon">{timeOfDayIcon}</span>
        <span className="timeline-batch__time-range">{timeRange}</span>
        {hasDetails && (
          <span className="timeline-batch__chevron">
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        )}
      </button>

      {/* Batch Items */}
      <div className="timeline-batch__items">
        {batch.items.map((item, index) => (
          <TimelineItemRow
            key={item.type === 'dose' ? item.data.id : `apt-${item.data.id}-${index}`}
            item={item}
            onDoseTaken={() => handleDoseAction(item, 'taken')}
            onDoseMissed={() => handleDoseAction(item, 'missed')}
            isLoading={isLoading}
            showActions={item.type === 'dose'}
          />
        ))}
      </div>

      {/* Expanded Details */}
      {isExpanded && hasDetails && (
        <div className="timeline-batch__details">
          {batch.items.map((item, index) => (
            <TimelineItemDetail
              key={item.type === 'dose' ? `detail-${item.data.id}` : `apt-detail-${item.data.id}-${index}`}
              item={item}
            />
          ))}
        </div>
      )}
    </div>
  )
}
